import { useEffect, useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';

/**
 * Navigation state stored in browser history
 */
interface NavigationState {
  tab: string;
  wellId?: string | null;
  subView?: string | null;
  timestamp: number;
}

/**
 * Hook configuration options
 */
interface UseNavigationHistoryOptions {
  /** The default/home tab to return to when history is exhausted */
  defaultTab?: string;
  /** Show "Press back again to exit" toast instead of allowing exit */
  preventExit?: boolean;
  /** Callback when user confirms exit (pressed back twice) */
  onExit?: () => void;
  /** Time window (ms) for double-back to exit. Default: 2000ms */
  exitTimeWindow?: number;
  /** Show confirmation dialog instead of toast when pressing back twice. Default: true */
  showExitDialog?: boolean;
}

/**
 * useNavigationHistory - Enables proper back button behavior in PWA/SPA
 * 
 * This hook syncs your app's navigation state with the browser's History API,
 * allowing the hardware/software back button to navigate within the app
 * instead of exiting.
 * 
 * @example
 * ```tsx
 * const [activeTab, setActiveTab] = useState('dashboard');
 * const [selectedWell, setSelectedWell] = useState(null);
 * 
 * useNavigationHistory({
 *   activeTab,
 *   setActiveTab,
 *   selectedWellId: selectedWell?.id,
 *   setSelectedWellId: (id) => setSelectedWell(id ? wells.find(w => w.id === id) : null),
 *   options: { preventExit: true, defaultTab: 'dashboard' }
 * });
 * ```
 */
export function useNavigationHistory({
  activeTab,
  setActiveTab,
  selectedWellId,
  setSelectedWellId,
  subView,
  setSubView,
  path,
  options = {}
}: {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  selectedWellId?: string | null;
  setSelectedWellId?: (id: string | null) => void;
  subView?: string | null;
  setSubView?: (view: string | null) => void;
  path?: string;
  options?: UseNavigationHistoryOptions;
}) {
  const {
    defaultTab = 'dashboard',
    preventExit = true,
    onExit,
    exitTimeWindow = 2000,
    showExitDialog = true
  } = options;

  // State for showing exit confirmation dialog
  const [showExitConfirmDialog, setShowExitConfirmDialog] = useState(false);

  // Track if we're handling a popstate event to prevent pushing duplicate states
  const isPopStateRef = useRef(false);
  // Track last back button press for "press again to exit" feature
  const lastBackPressRef = useRef(0);
  // Track if we've initialized the history
  const isInitializedRef = useRef(false);
  // Previous state to detect actual changes
  const prevStateRef = useRef({ activeTab, selectedWellId, subView });

  /**
   * Push a new state to browser history
   */
  const pushState = useCallback((state: NavigationState) => {
    try {
      window.history.pushState(state, '', path || window.location.pathname);
    } catch (error) {
      console.warn('[useNavigationHistory] Failed to push state:', error);
    }
  }, [path]);

  /**
   * Replace current history state (used for initialization)
   */
  const replaceState = useCallback((state: NavigationState) => {
    try {
      window.history.replaceState(state, '', path || window.location.pathname);
    } catch (error) {
      console.warn('[useNavigationHistory] Failed to replace state:', error);
    }
  }, [path]);

  // Initialize history on mount
  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    const initialState: NavigationState = {
      tab: activeTab,
      wellId: selectedWellId,
      subView: subView,
      timestamp: Date.now()
    };

    // Replace current state with our initial state
    replaceState(initialState);
    prevStateRef.current = { activeTab, selectedWellId, subView };
  }, []); // Only run once on mount

  // Push state when navigation changes
  useEffect(() => {
    // Skip if this is a popstate-triggered change
    if (isPopStateRef.current) {
      isPopStateRef.current = false;
      return;
    }

    // Skip if nothing actually changed
    const prev = prevStateRef.current;
    if (
      prev.activeTab === activeTab &&
      prev.selectedWellId === selectedWellId &&
      prev.subView === subView
    ) {
      return;
    }

    // Update previous state
    prevStateRef.current = { activeTab, selectedWellId, subView };

    // Push new state to history
    const newState: NavigationState = {
      tab: activeTab,
      wellId: selectedWellId,
      subView: subView,
      timestamp: Date.now()
    };

    pushState(newState);
  }, [activeTab, selectedWellId, subView, pushState]);

  // Handle back button (popstate event)
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const state = event.state as NavigationState | null;

      // If we have a valid state, restore it
      if (state && state.tab) {
        isPopStateRef.current = true;
        prevStateRef.current = {
          activeTab: state.tab,
          selectedWellId: state.wellId,
          subView: state.subView
        };

        // Restore tab
        setActiveTab(state.tab);

        // Restore selected well if setter provided
        if (setSelectedWellId) {
          setSelectedWellId(state.wellId || null);
        }

        // Restore sub-view if setter provided
        if (setSubView) {
          setSubView(state.subView || null);
        }

        return;
      }

      // No state means we're at the beginning of history (user trying to exit)
      if (preventExit) {
        const now = Date.now();
        
        if (now - lastBackPressRef.current < exitTimeWindow) {
          // User pressed back twice quickly - show confirmation dialog or call onExit
          if (showExitDialog) {
            // Push state to prevent actual exit while dialog is shown
            const currentState: NavigationState = {
              tab: activeTab,
              wellId: selectedWellId,
              subView: subView,
              timestamp: Date.now()
            };
            pushState(currentState);
            
            // Show confirmation dialog
            setShowExitConfirmDialog(true);
            return;
          }
          
          // No dialog - just call onExit if provided
          if (onExit) {
            onExit();
          }
          // Don't prevent the navigation - let the app exit
          return;
        }

        // First back press at root - show toast and push state to prevent exit
        lastBackPressRef.current = now;
        
        // Push a state to prevent actual exit
        const currentState: NavigationState = {
          tab: activeTab,
          wellId: selectedWellId,
          subView: subView,
          timestamp: Date.now()
        };
        pushState(currentState);

        toast.info('Press back again to exit', {
          duration: 2000,
          id: 'exit-toast' // Prevent duplicate toasts
        });

        return;
      }

      // preventExit is false - navigate to default tab or allow exit
      if (activeTab !== defaultTab) {
        isPopStateRef.current = true;
        prevStateRef.current = { activeTab: defaultTab, selectedWellId: null, subView: null };
        setActiveTab(defaultTab);
        if (setSelectedWellId) setSelectedWellId(null);
        if (setSubView) setSubView(null);
        
        // Push state to prevent immediate exit
        pushState({
          tab: defaultTab,
          wellId: null,
          subView: null,
          timestamp: Date.now()
        });
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [
    activeTab,
    selectedWellId,
    subView,
    setActiveTab,
    setSelectedWellId,
    setSubView,
    defaultTab,
    preventExit,
    onExit,
    exitTimeWindow,
    showExitDialog,
    pushState
  ]);

  /**
   * Programmatically go back in history
   */
  const goBack = useCallback(() => {
    window.history.back();
  }, []);

  /**
   * Clear history and reset to default state
   */
  const resetHistory = useCallback(() => {
    const resetState: NavigationState = {
      tab: defaultTab,
      wellId: null,
      subView: null,
      timestamp: Date.now()
    };
    replaceState(resetState);
    prevStateRef.current = { activeTab: defaultTab, selectedWellId: null, subView: null };
  }, [defaultTab, replaceState]);

  /**
   * Handle user confirming exit from the dialog
   */
  const handleConfirmExit = useCallback(() => {
    setShowExitConfirmDialog(false);
    // Reset the last back press time
    lastBackPressRef.current = 0;
    // Call onExit callback if provided
    if (onExit) {
      onExit();
    }
    // Close the app/window - this works in PWA mode
    // For web, we can try to close the window or navigate away
    try {
      window.close();
    } catch {
      // If window.close() doesn't work (not opened by script), 
      // navigate to a blank page or just return to let onExit handle it
    }
  }, [onExit]);

  /**
   * Handle user canceling exit from the dialog
   */
  const handleCancelExit = useCallback(() => {
    setShowExitConfirmDialog(false);
    // Reset the last back press time so they need to double-press again
    lastBackPressRef.current = 0;
  }, []);

  return {
    /** Programmatically trigger back navigation */
    goBack,
    /** Reset history to initial state */
    resetHistory,
    /** Whether the exit confirmation dialog should be shown */
    showExitConfirmDialog,
    /** Call this when user confirms they want to exit */
    handleConfirmExit,
    /** Call this when user cancels exit */
    handleCancelExit
  };
}

export default useNavigationHistory;
